/**
 * Fixed-stage coordinate helpers.
 *
 * The whole game is composed at a constant 1600×900 design resolution inside
 * `#game-container`, which an inline script in index.html scales to fit the
 * viewport (`transform: translate()+scale()`) and publishes as `window.__stageFit`.
 *
 * Most UI lives INSIDE that transformed stage, so it scales automatically. A few
 * helpers (the hover tooltip, the tower info panel) position themselves from
 * `getBoundingClientRect()` — which returns *viewport* (post-transform) coords —
 * yet set `style.left/top` that are interpreted in the stage's *local* (design)
 * space. These helpers use {@link clientPointToStage} / {@link clientLenToStage}
 * to bridge the two spaces so they land precisely and scale with everything else.
 */
/** Per-side safe-area insets (physical viewport px) reserved by the fit. */
export interface StageInsets {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface StageFit {
  /** Uniform scale applied to the design stage to fit the viewport. */
  scale: number;
  /** Viewport-px offset of the scaled stage's top-left (letterbox margin). */
  offsetX: number;
  offsetY: number;
  /** Design-space dimensions of the stage. */
  designW: number;
  designH: number;
  /**
   * Aspect-pinned rail widths (design px) the inline fit() resolved. Surfaced
   * so phone-tier chrome (bottom action bar, reflow) can read them without
   * re-deriving the rail math. Fallback to 0 when the stage hasn't published.
   */
  leftRail: number;
  rightRail: number;
  /**
   * Live per-side safe-area insets (post-min-gutter, physical px). The notch
   * swaps sides on a 180° landscape flip, so thumb-zone placement MUST read
   * these per-side rather than assume a static left/right. Fallback to zeros.
   */
  insets: StageInsets;
  /**
   * Phone-class (compact) tier, computed by the inline fit() from the device-
   * stable screen short edge so it is readable BEFORE the bundle (no-flash).
   * Mirror of {@link isCompactScreen}. Fallback `false` (tablet/desktop) when
   * unpublished — headless tests stay non-phone.
   */
  phoneTier: boolean;
}

const ZERO_INSETS: StageInsets = { top: 0, right: 0, bottom: 0, left: 0 };

interface StageFitGlobal {
  __stageFit?: Partial<StageFit>;
}

/**
 * The live stage transform set by the index.html fit script. Falls back to an
 * identity fit (scale 1, viewport-sized) when there is no stage — headless/test
 * DOM, or before the script has run — so callers behave exactly as they did
 * pre-stage in that case.
 *
 * The rail/inset/phoneTier fields are surfaced additively: pre-stage they fall
 * back to 0 / zero-insets / false, so existing callers (which only read
 * scale/offset/design dims) are byte-identical.
 */
export function getStageFit(): StageFit {
  const f = (globalThis as StageFitGlobal).__stageFit;
  const vw = typeof window !== "undefined" ? window.innerWidth || 0 : 0;
  const vh = typeof window !== "undefined" ? window.innerHeight || 0 : 0;
  if (f && typeof f.scale === "number" && f.scale > 0) {
    return {
      scale: f.scale,
      offsetX: f.offsetX ?? 0,
      offsetY: f.offsetY ?? 0,
      designW: f.designW ?? vw,
      designH: f.designH ?? vh,
      leftRail: f.leftRail ?? 0,
      rightRail: f.rightRail ?? 0,
      insets: f.insets ?? ZERO_INSETS,
      phoneTier: f.phoneTier ?? false,
    };
  }
  return {
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    designW: vw,
    designH: vh,
    leftRail: 0,
    rightRail: 0,
    insets: ZERO_INSETS,
    phoneTier: false,
  };
}

/**
 * Convert a viewport (client) point — e.g. from `getBoundingClientRect()` — into
 * the stage's local design-space coordinates, for positioning an element that
 * lives inside the transformed stage.
 */
export function clientPointToStage(
  clientX: number,
  clientY: number,
): { x: number; y: number } {
  const f = getStageFit();
  return { x: (clientX - f.offsetX) / f.scale, y: (clientY - f.offsetY) / f.scale };
}

/** Convert a viewport length into a stage-local (design-space) length. */
export function clientLenToStage(len: number): number {
  return len / getStageFit().scale;
}
